import { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AssetType, CreativeFilters, GroupBy, Level, MediaFilter } from './types';
import { DEFAULT_FILTERS, PAGE_SIZE_OPTIONS } from './utils';
import { parseRules, serializeRules } from './filterRules';

// Filter state lives in the URL so a view can be pasted into Slack and open the
// same table for someone else. Modelled on AdLibrary/AdBrowser's replaceUrl +
// suppressUrlSync pair: we write to the URL on change, and read back only when
// the change came from outside (browser back/forward), never from our own write.

const MEDIA_TYPES: MediaFilter[] = ['all', 'video', 'static', 'carousel', 'unknown'];
const GROUP_BYS: GroupBy[] = ['ad', 'creative', 'headline', 'primary_text'];
const LEVELS: Level[] = ['ad', 'asset'];
const ASSET_TYPES: AssetType[] = ['headline', 'primary_text', 'image', 'video'];

function isIsoDate(value: string | null): value is string {
  return !!value && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function oneOf<T extends string>(value: string | null, allowed: T[], fallback: T): T {
  return allowed.includes(value as T) ? (value as T) : fallback;
}

function parse(search: string): CreativeFilters {
  const p = new URLSearchParams(search);
  return {
    start: isIsoDate(p.get('start')) ? (p.get('start') as string) : DEFAULT_FILTERS.start,
    end: isIsoDate(p.get('end')) ? (p.get('end') as string) : DEFAULT_FILTERS.end,
    mediaType: oneOf(p.get('mediaType'), MEDIA_TYPES, DEFAULT_FILTERS.mediaType),
    groupBy: oneOf(p.get('groupBy'), GROUP_BYS, DEFAULT_FILTERS.groupBy),
    search: p.get('q') || '',
    sort: p.get('sort') || DEFAULT_FILTERS.sort,
    direction: p.get('direction') === 'asc' ? 'asc' : 'desc',
    level: oneOf(p.get('level'), LEVELS, DEFAULT_FILTERS.level),
    assetType: oneOf(p.get('assetType'), ASSET_TYPES, DEFAULT_FILTERS.assetType),
    // Absent means the default (on); only an explicit "0" turns it off.
    hideZeroSpend: p.get('zero') === null ? DEFAULT_FILTERS.hideZeroSpend : p.get('zero') !== '0',
    pageSize: PAGE_SIZE_OPTIONS.includes(Number(p.get('pageSize')))
      ? Number(p.get('pageSize'))
      : DEFAULT_FILTERS.pageSize,
    // Rules are validated against the live columns by the caller, which knows
    // the current level; anything that fails there is dropped without comment.
    rules: parseRules(p.get('f')),
  };
}

// Only non-default values are written, so a freshly opened page has a clean URL.
function serialize(filters: CreativeFilters): string {
  const p = new URLSearchParams();
  const put = (key: string, value: string | number, fallback: string | number) => {
    if (value !== fallback && value !== '' && value !== 0) p.set(key, String(value));
  };
  // The date range is always written, even when it matches the default. The
  // default is "the trailing 7 days" resolved when the page loaded, so a link
  // that omitted it would silently show a different window tomorrow — which is
  // exactly what someone pasting a view does not want.
  p.set('start', filters.start);
  p.set('end', filters.end);
  put('mediaType', filters.mediaType, DEFAULT_FILTERS.mediaType);
  put('groupBy', filters.groupBy, DEFAULT_FILTERS.groupBy);
  put('q', filters.search, '');
  put('sort', filters.sort, DEFAULT_FILTERS.sort);
  put('direction', filters.direction, DEFAULT_FILTERS.direction);
  put('level', filters.level, DEFAULT_FILTERS.level);
  put('assetType', filters.assetType, DEFAULT_FILTERS.assetType);
  put('pageSize', filters.pageSize, DEFAULT_FILTERS.pageSize);
  if (filters.rules.length > 0) p.set('f', serializeRules(filters.rules));
  if (filters.hideZeroSpend !== DEFAULT_FILTERS.hideZeroSpend) {
    p.set('zero', filters.hideZeroSpend ? '1' : '0');
  }
  return p.toString();
}

// `active`: false while the page is mounted but hidden behind another tab, whose
// URL (and query string) isn't this page's to read.
export default function useUrlFilters(active = true): [
  CreativeFilters,
  (patch: Partial<CreativeFilters>) => void,
  () => void,
] {
  const history = useHistory();
  const location = useLocation();
  const [filters, setFilters] = useState<CreativeFilters>(() => parse(location.search));
  const suppressUrlSync = useRef(false);

  const update = useCallback(
    (patch: Partial<CreativeFilters>) => {
      setFilters(current => {
        const next = { ...current, ...patch };
        const search = serialize(next);
        suppressUrlSync.current = true;
        history.replace({
          pathname: location.pathname,
          search: search ? `?${search}` : '',
        });
        return next;
      });
    },
    [history, location.pathname],
  );

  const reset = useCallback(() => {
    suppressUrlSync.current = true;
    history.replace({ pathname: location.pathname, search: '' });
    setFilters(parse(''));
  }, [history, location.pathname]);

  // Back/forward should restore the previous view; our own writes should not
  // loop back through here.
  useEffect(() => {
    if (!active) return;
    if (suppressUrlSync.current) {
      suppressUrlSync.current = false;
      return;
    }
    setFilters(parse(location.search));
  }, [location.search, active]);

  return [filters, update, reset];
}
